import { Button as CompassButton } from "@circleco/compass/components/Button";
import type { MenuProps } from "@circleco/compass/components/Menu";
import { SplitButton as CompassSplitButton } from "@circleco/compass/components/SplitButton";
import isFunction from "lodash/isFunction";
import { useCallback, useMemo } from "react";

import type { ButtonVariant } from "@/react/components/SettingsApp/CompassCompat/Button";
import { ActionSelectorButton as UIKitActionSelectorButton } from "@/react/components/shared/uikit/ActionSelectorButton";
import { useIsCircleV4FeatureEnabled } from "@/react/hooks/useIsCircleV4FeatureEnabled";

export interface Option {
  icon?: string;
  label: string;
  onClick?: (...args: any[]) => void;
  value: string;
}

export { SplitButton as ActionSelectorButton };

const SPLIT_VARIANT_MAP: Record<string, "primary" | "outline"> = {
  secondary: "outline",
  "secondary-light": "outline",
  "danger-outline": "primary",
  danger: "primary",
};

const BUTTON_VARIANT_MAP: Record<
  string,
  "primary" | "outline" | "destructive"
> = {
  secondary: "outline",
  "secondary-light": "outline",
  danger: "destructive",
  "danger-outline": "destructive",
};

const mapSplitVariant = (variant?: ButtonVariant): "primary" | "outline" =>
  SPLIT_VARIANT_MAP[variant ?? ""] ?? "primary";

const mapButtonVariant = (
  variant?: ButtonVariant,
): "primary" | "outline" | "destructive" =>
  BUTTON_VARIANT_MAP[variant ?? ""] ?? "primary";

interface SplitButtonProps {
  disabled?: boolean;
  large?: boolean;
  onChange?: (action?: Option) => void;
  options: Option[];
  selectedAction?: Option;
  setSelectedAction?: (action?: Option) => void;
  small?: boolean;
  type?: "button" | "submit" | "reset";
  variant?: ButtonVariant;
}

const SplitButton = ({
  disabled = false,
  large = false,
  small = false,
  onChange,
  options,
  selectedAction,
  setSelectedAction,
  type = "button",
  variant,
}: SplitButtonProps) => {
  const isCircleV4 = useIsCircleV4FeatureEnabled("circle_v4_buttons_enabled");

  const selectedOption = useMemo(
    () =>
      options.find(opt => opt.value === selectedAction?.value) ?? options[0],
    [options, selectedAction?.value],
  );

  const secondaryOptions = useMemo(
    () => options.filter(opt => opt.value !== selectedOption?.value),
    [options, selectedOption?.value],
  );

  const menuOptions: MenuProps["options"] = useMemo(
    () =>
      secondaryOptions.map(opt => ({
        label: opt.label,
        onClick: () => {
          setSelectedAction?.(opt);
          if (isFunction(onChange)) {
            onChange(opt);
          } else if (isFunction(opt.onClick)) {
            opt.onClick();
          }
        },
      })),
    [secondaryOptions, setSelectedAction, onChange],
  );

  const handlePrimaryClick = useCallback(() => {
    if (isFunction(selectedOption?.onClick)) {
      selectedOption.onClick();
    }
  }, [selectedOption]);

  if (!isCircleV4) {
    return (
      <UIKitActionSelectorButton
        disabled={disabled}
        large={large}
        onChange={onChange}
        options={options}
        selectedAction={selectedAction}
        setSelectedAction={setSelectedAction ?? (() => {})}
        type={type}
        variant={variant}
      />
    );
  }

  const size = (() => {
    if (large) return "lg";
    if (small) return "sm";
    return "md";
  })();

  if (secondaryOptions.length === 0) {
    return (
      <CompassButton
        variant={mapButtonVariant(variant)}
        size={size}
        disabled={disabled}
        type={type}
        onClick={handlePrimaryClick}
        data-testid="perform-action-button"
      >
        {selectedOption?.label}
      </CompassButton>
    );
  }

  return (
    <CompassSplitButton
      variant={mapSplitVariant(variant)}
      size={size}
      disabled={disabled}
      type={type}
      onClick={handlePrimaryClick}
      menuOptions={menuOptions}
      data-testid="perform-action-button"
    >
      {selectedOption?.label}
    </CompassSplitButton>
  );
};
